import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, ApolloError } from '@apollo/client';
import { Spin, App } from 'antd';
import { queries, mutations } from '../../../graphql/graphql';
import { DirectoryInput, UpdateDirectoryResponse } from '../../../types/directory';
import DirectoryForm from '../../../components/Directory/DirectoryForm';
import FormLayout from '../../../layouts/CardLayout';

const DirectoryEdit: React.FC = () => {
  const { message } = App.useApp();
  const { id } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const {
    data: directoryData,
    loading: fetchLoading,
    refetch,
  } = useQuery(queries.directories.DIRECTORY, {
    variables: { id },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  const [updateDirectory, { loading: updateLoading }] = useMutation<
    UpdateDirectoryResponse,
    { id: string; input: DirectoryInput }
  >(mutations.directories.UPDATE_DIRECTORY);

  const handleSubmit = async (values: DirectoryInput) => {
    try {
      const { data } = await updateDirectory({
        variables: { id: id as string, input: values },
      });

      if (data?.updateDirectory) {
        message.success('Directory successfully updated');
        navigate(`/admin/directory/${id}`);
      }
    } catch (err: unknown) {
      if ((err as ApolloError).graphQLErrors?.length > 0) {
        const graphQLError = (err as ApolloError).graphQLErrors[0];
        setError(
          graphQLError.message.includes('slug')
            ? 'Slug has already been taken.'
            : graphQLError.message
        );
      } else {
        setError('An unexpected error occurred');
      }
    }
  };

  if (fetchLoading) {
    return <Spin size="large" />;
  }

  return (
    <FormLayout>
      <DirectoryForm
        initialValues={directoryData?.directory}
        onSubmit={handleSubmit}
        error={error}
        loading={updateLoading}
        submitButtonText="Update"
      />
    </FormLayout>
  );
};

export default DirectoryEdit;
